// src/modules/admin/components/AdminLayout/index.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../../../contexts/ThemeContext';
import AdminSidebar from '../AdminSidebar';
import './styles.css';

function useMediaQuery(query) {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) setMatches(media.matches);
        const listener = (e) => setMatches(e.matches);
        media.addEventListener('change', listener);
        return () => media.removeEventListener('change', listener);
    }, [matches, query]);
    return matches;
}

const AdminLayout = ({ children, title }) => {
    const { theme } = useTheme();
    const isDesktop = useMediaQuery('(min-width: 992px)');
    const [showSidebar, setShowSidebar] = useState(false);

    const toggleSidebar = () => {
        setShowSidebar(prev => !prev);
    };

    const closeSidebar = () => {
        setShowSidebar(false);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            closeSidebar();
        }
    };

    return (
        <div className={`admin-layout ${theme}`}>
            {/* Sidebar — desktop: always visible; mobile: offcanvas overlay */}
            <div
                className={`admin-sidebar-wrapper ${isDesktop ? 'sidebar-desktop' : ''} ${showSidebar ? 'sidebar-open' : ''}`}
            >
                <AdminSidebar />
            </div>

            {/* Backdrop for mobile */}
            {!isDesktop && showSidebar && (
                <div
                    className="admin-backdrop"
                    onClick={handleBackdropClick}
                    aria-hidden="true"
                />
            )}

            {/* Main content area */}
            <div className="admin-main">
                {/* Page header */}
                <header className="admin-page-header">
                    <div className="admin-header-left">
                        {/* Hamburger toggle — mobile only */}
                        {!isDesktop && (
                            <button
                                className="admin-hamburger-btn"
                                onClick={toggleSidebar}
                                aria-label="Toggle navigation"
                            >
                                <i className={`bi ${showSidebar ? 'bi-x-lg' : 'bi-list'}`}></i>
                            </button>
                        )}
                        {/* Page title */}
                        {title && <h1 className="admin-page-title">{title}</h1>}
                    </div>
                    <div className="admin-header-right">
                        {/* Refresh button area */}
                        <button
                            className="admin-refresh-btn"
                            aria-label="Refresh page"
                            onClick={() => window.location.reload()}
                        >
                            <i className="bi bi-arrow-clockwise"></i>
                        </button>
                    </div>
                </header>

                {/* Page content */}
                <main className="admin-content">
                    {children}
                </main>
            </div>
        </div>
    );
};

AdminLayout.propTypes = {
    children: PropTypes.node.isRequired,
    title: PropTypes.string,
};

export default AdminLayout;
